import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { AprMark } from './BrandMarks';
import { NEXT_STEPS } from '../utils/campaign';
import { openGiveFromClick } from '../utils/giving';
import { TRACK_OPEN } from '../utils/giveCore';
import { typeOnSteps, shownAt, markTyped } from '../utils/typeOn';
import { VOICE_NAMES, FALLBACK_NOTES } from '../utils/teamVoice';
import { cleanQuote, quoteLabel } from '../utils/quote';

/*
 * The pieces of a conversation in the printed-interview layout (BRAND_SPEC
 * §11.1): no bubbles. A person's words are set in the serif, AI Rob's in the
 * sans, and every AI turn says what it is.
 */

/**
 * "AI Rob · from 2 sermons" above every AI turn, with the 16 px mark; "from
 * Rob's sermons" until the sources are known. The team's framing (Oct 2026):
 * the AI draws the answer from Rob's own teaching, so the label says where.
 */
export function AiLabel({ suffix = '' }) {
  return (
    <div className="cv-label cv-label--ai">
      <AprMark height={16} />
      <span>AI Rob · {suffix || 'from Rob’s sermons'}</span>
    </div>
  );
}

/** "from 3 sermons", "from 2 sermons and 1 post", "from Rob's book". */
export function sourceSummary(kinds) {
  const parts = [];
  if (kinds.video) parts.push(`${kinds.video} sermon${kinds.video === 1 ? '' : 's'}`);
  if (kinds.blog) parts.push(`${kinds.blog} post${kinds.blog === 1 ? '' : 's'}`);
  if (kinds.book) parts.push('Rob’s book');
  if (!parts.length) return '';
  const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `from ${list}`;
}

/** Size class for a person's words: short questions read as a heading. */
function questionSize(text, first) {
  const n = (text || '').length;
  if (first && n <= 90) return 'size-lead';
  if (n <= 180) return 'size-follow';
  return 'size-long';
}

/**
 * One thing a person said. The first question of a conversation is the
 * view's heading; an ad's question says so, with a way to put it their own way.
 * A question about highlighted words ("Ask about this", utils/quote.js) shows
 * them first, in the sans and set off as a quote: AI Rob's words above, the
 * person's own below in the serif. Its size goes by their words alone. In the
 * page the quote comes after the question, so a screen reader that jumps to
 * the heading hears what "this" is next; ChatView.css shows it above.
 */
export function UserTurn({ message, first, onEdit }) {
  const landing = Boolean(message.landing);
  const Tag = first ? 'h1' : 'h2';
  const quote = cleanQuote(message.quote);
  return (
    <div className={`cv-msg user${landing ? ' landing' : ''}${quote ? ' quoting' : ''}`} data-key={message.timestamp}>
      <div className="cv-label">{landing ? 'Your question' : 'You'}</div>
      <Tag className={`cv-question ${questionSize(message.content, first)}`}>{message.content}</Tag>
      {quote && (
        <figure className="cv-quote is-turn">
          <figcaption className="cv-quote-label">{quoteLabel(quote)}</figcaption>
          <blockquote className="cv-quote-text" data-no-track>{quote.text}</blockquote>
        </figure>
      )}
      {landing && (
        <p className="cv-arrival">
          We asked this for you from the ad. Want to put it your own way?{' '}
          <button type="button" className="cv-arrival-edit" onClick={() => onEdit(message.content)}>Edit</button>
        </p>
      )}
    </div>
  );
}

/**
 * "Ask about this" (utils/quote.js), at the top of the composer while words in
 * a finished answer are selected: never over the words, their handles or the
 * phone's own Copy menu, and in the same place on every device. It never
 * shows the words; the hint says what a tap does. A tap puts them in a quote
 * above the box (QuoteChip).
 */
export function AskOffer({ onAsk, hintId }) {
  return (
    <div className="cv-ask-offer-row">
      <button type="button" className="cv-ask-offer" onClick={onAsk} data-track="Ask about this" aria-describedby={hintId}>
        <Icon name="text-quote" size={16} />
        <span>Ask about this</span>
      </button>
      <span id={hintId} className="apr-sr-only">Puts the words you selected above the message box, so you can ask about them.</span>
    </div>
  );
}

/**
 * The words someone chose to ask about, above the box until they send or
 * remove them: what they are, the words between quotation marks (two lines at
 * most, one on a short screen) and Remove. The box's aria-describedby points
 * at `id`, so a screen reader hears them with the box.
 */
export function QuoteChip({ quote, id, onRemove }) {
  return (
    <div className="cv-quote-chip">
      <div className="cv-quote is-chip" id={id}>
        <span className="cv-quote-label">{quoteLabel(quote)}</span>
        <span className="cv-quote-text" data-no-track>{quote.text}</span>
      </div>
      <button type="button" className="cv-quote-remove" onClick={onRemove} aria-label="Remove quote" data-track="Remove quote">
        <Icon name="x" size={20} />
      </button>
    </div>
  );
}

/**
 * The welcome's heading, typed out once a visit (BRAND_SPEC §9.1 and §9.6,
 * utils/typeOn.js), the caret after the last character while it types.
 *
 * The words are real text from the first paint. While they type they are
 * transparent under an aria-hidden copy, laid out exactly like them, that
 * shows them a character at a time: a screen reader hears "Ask anything."
 * once, whole, and nothing moves when the copy goes. The typing starts when
 * the heading is really on screen, and stops at once (the words, whole) when
 * `stop` turns true: the box has focus or words in it. `onStart()` runs once,
 * as the typing starts: the first frame someone sees (the rest of the first
 * view is timed from it, ChatView). `onDone(typed)` runs once: `typed` is
 * true when the caret drops to its your-turn place, false when the typing
 * was cut short or never ran (very large text, or the heading went).
 */
export function WelcomeTitle({ text, animate, stop, onStart, onDone }) {
  const [shown, setShown] = useState(() => (animate ? typeOnSteps(text).steps[0].shown : null));
  const titleRef = useRef(null);
  const doneRef = useRef(!animate);
  const goneTimerRef = useRef(null);
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const finishRef = useRef(null);
  finishRef.current = (typed = false) => {
    setShown(null);
    if (doneRef.current) return;
    doneRef.current = true;
    if (onDoneRef.current) onDoneRef.current(typed);
  };

  // Very large text wraps the words onto two lines: no typing, the words whole.
  useLayoutEffect(() => {
    const words = titleRef.current && titleRef.current.firstChild;
    if (!doneRef.current && words && words.getClientRects().length > 1) finishRef.current();
  }, []);

  useEffect(() => {
    clearTimeout(goneTimerRef.current); // StrictMode's second mount: still here
    if (doneRef.current) return undefined;
    const { steps, settleAt } = typeOnSteps(text);
    let raf = 0;
    let t0 = null;
    let started = false;
    let onScreen = typeof IntersectionObserver === 'undefined';
    const tick = (now) => {
      if (doneRef.current) return; // stopped (the box took focus)
      if (t0 == null) t0 = now;
      const ms = now - t0;
      if (ms >= settleAt) {
        finishRef.current(true);
        return;
      }
      setShown(shownAt(steps, ms));
      raf = requestAnimationFrame(tick);
    };
    // Typed for someone to see: not behind another view, not in a background tab.
    const start = () => {
      if (started || !onScreen || doneRef.current || document.visibilityState === 'hidden') return;
      started = true;
      markTyped();
      if (onStartRef.current) onStartRef.current();
      raf = requestAnimationFrame(tick);
    };
    const io = onScreen ? null : new IntersectionObserver((entries) => {
      onScreen = entries[entries.length - 1].isIntersecting;
      start();
    });
    if (io && titleRef.current) io.observe(titleRef.current);
    document.addEventListener('visibilitychange', start);
    start();
    return () => {
      cancelAnimationFrame(raf);
      if (io) io.disconnect();
      document.removeEventListener('visibilitychange', start);
      // Gone before the end (a starter was tapped): done, unless this was only
      // StrictMode trying the effect twice, which mounts it again at once.
      goneTimerRef.current = setTimeout(() => {
        if (doneRef.current) return;
        doneRef.current = true;
        if (onDoneRef.current) onDoneRef.current(false);
      }, 0);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (stop && !doneRef.current) finishRef.current();
  }, [stop]);

  return (
    <h1 className={`cv-welcome-title${shown != null ? ' is-typing' : ''}`} ref={titleRef}>
      <span className="cv-title-words">{text}</span>
      {shown != null && (
        <span className="cv-title-typed" aria-hidden="true">
          {text.slice(0, shown)}
          {/* A new caret for each character (keyed): one that moved along
              would be counted as the page shifting (Cumulative Layout Shift). */}
          <span className="cv-title-caret" key={shown} />
          <span className="cv-title-rest">{text.slice(shown)}</span>
        </span>
      )}
    </h1>
  );
}

/** Questions to start with: rows of a person's words, each one tap to ask. */
/**
 * Starter questions, one tap each. `glimmer` is the index of the one the
 * welcome screen's light is passing over (utils/starterGlimmer.js), or -1;
 * `onMore`, when given, adds "More questions" for another four.
 */
export function StarterRows({ questions, onAsk, disabled, label = 'Questions to start with', glimmer = -1, onMore = null }) {
  return (
    <>
      <ul className="cv-starters" aria-label={label}>
        {questions.map((text, i) => (
          <li key={text}>
            <button type="button" className={`cv-starter${i === glimmer ? ' is-glimmer' : ''}`} onClick={() => onAsk(text)} disabled={disabled}>
              <span>{text}</span>
              <Icon name="chevron-right" size={20} />
            </button>
          </li>
        ))}
      </ul>
      {onMore && (
        <button type="button" className="cv-more-starters" onClick={onMore} disabled={disabled} data-track="More questions">
          <Icon name="refresh-cw" size={16} />
          <span>More questions</span>
        </button>
      )}
    </>
  );
}

/**
 * First Answer's More (utils/experiments.js; Pastor Rob, Oct 20: "serve up a sentence on the first
 * question, then have a more? button"): under a short first answer, while it is the latest one.
 * One tap asks the same question again for the full answer, at Fast, like any question. It says
 * what it does and nothing about why the answer was short.
 */
export function MoreAnswer({ onMore, disabled = false }) {
  return (
    <div className="cv-more-answer-row">
      <button
        type="button"
        className="apr-btn cv-more-answer"
        onClick={onMore}
        disabled={disabled}
        aria-label="More: the full answer to your question"
        data-track="More (first answer)"
      >
        <span>More</span>
        <Icon name="chevron-down" size={20} />
      </button>
    </div>
  );
}

/**
 * The next-step ladder, "When you're ready" (BRAND_SPEC §11.1; TEAM_APP_SPEC
 * §8.1 words): after the first answer of a campaign landing, in the
 * question's order. The first choice is the 56 px line button.
 */
export function NextSteps({ order, onChoose, innerRef }) {
  return (
    <section className="cv-ladder" ref={innerRef} aria-labelledby="cv-ladder-label">
      <div className="cv-label" id="cv-ladder-label">When you’re ready</div>
      {order.filter((kind) => NEXT_STEPS[kind]).map((kind, i) => (
        <div className="cv-step" key={kind}>
          <button
            type="button"
            className={`apr-btn ${i === 0 ? 'apr-btn--lg' : 'apr-btn--quiet'} apr-btn--block cv-step-btn`}
            onClick={() => onChoose(kind)}
            aria-describedby={`cv-step-${kind}`}
          >
            {NEXT_STEPS[kind].label}
          </button>
          <p className="cv-step-desc" id={`cv-step-${kind}`}>{NEXT_STEPS[kind].description}</p>
        </div>
      ))}
    </section>
  );
}

/**
 * The docked human door (BRAND_SPEC §11.1): a 48 px bar above the composer,
 * only while real people are taking requests.
 */
export function HumanDoor({ onMeet }) {
  return (
    <div className="cv-door" role="region" aria-label="Talk with a real person">
      <Icon name="user-round" size={20} />
      <span className="cv-door-text">A real person, when you want one.</span>
      <button type="button" className="cv-door-btn" onClick={onMeet}>Meet one</button>
    </div>
  );
}

/**
 * The real Rob, recorded (BRAND_SPEC §10.3, §11.1): natural colour, captions
 * on, tap to play, never autoplay. Shown only once a cut exists for the
 * question's territory (see utils/robVideo.js).
 */
export function RobVideoCard({ video }) {
  const [playing, setPlaying] = useState(false);
  return (
    <section className="cv-rob" aria-label="The real Rob, recorded, not AI">
      <div className="cv-label">The real Rob · recorded, not AI</div>
      <div className="cv-rob-media">
        {playing ? (
          <video src={video.src} poster={video.poster} controls autoPlay playsInline>
            {video.captions && <track kind="captions" src={video.captions} srcLang="en" label="English" default />}
          </video>
        ) : (
          <button type="button" className="cv-rob-play" onClick={() => setPlaying(true)} aria-label={`Play: ${video.title || 'a minute from Rob'}`}>
            {video.poster && <img src={video.poster} alt="" />}
            {video.placeholder && <span className="cv-rob-placeholder">{video.placeholder}</span>}
            <span className="cv-rob-play-ring" aria-hidden="true"><Icon name="play" size={20} strokeWidth={2} /></span>
          </button>
        )}
      </div>
      <div className="cv-rob-meta">{video.meta || 'A minute from Rob · 1:00 · captions on'}</div>
    </section>
  );
}

/** A 2 px white line that follows the microphone's level while recording. */
export function VoiceLevel({ stream }) {
  const lineRef = useRef(null);
  useEffect(() => {
    const line = lineRef.current;
    if (!stream || !line) return undefined;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return undefined;
    let ctx;
    let raf = 0;
    try {
      ctx = new Ctx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i] - 128));
        line.style.transform = `scaleX(${Math.min(1, 0.06 + (peak / 128) * 1.6).toFixed(3)})`;
        raf = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) { /* no level: the static line stays */ }
    return () => {
      cancelAnimationFrame(raf);
      if (ctx) ctx.close().catch(() => {});
    };
  }, [stream]);
  return <span className="cv-voice-level" aria-hidden="true"><span ref={lineRef} /></span>;
}

/**
 * While AI Rob's reply is read aloud: what is speaking, said plainly
 * (BRAND_SPEC §11.1 voice mode), with Pause, Stop and Type instead.
 */
export function VoiceBar({ state, teamVoice = null, heard = null, onPause, onResume, onStop, onTypeInstead }) {
  const label = state === 'loading'
    ? 'Preparing AI Rob’s voice…'
    : state === 'paused'
      ? 'Paused · AI Rob, in Rob’s voice'
      : 'AI Rob is speaking, in Rob’s voice';
  // The team only (teamVoice is their pick; null for a visitor): which voice is reading, and the
  // one-line note when ElevenLabs was picked and Grok read instead (X-Voice-Fallback).
  const who = teamVoice ? VOICE_NAMES[(heard && heard.voice) || teamVoice] || null : null;
  const note = teamVoice && heard && heard.fallback ? FALLBACK_NOTES[heard.fallback] || null : null;
  return (
    <div className="cv-voice-bar" role="status">
      <span className="cv-voice-label">
        {label}
        {who && <span className="cv-voice-who"> · {who}</span>}
        {note && <span className="cv-voice-note">{note}</span>}
      </span>
      <div className="cv-voice-actions">
        {state === 'playing' && (
          <button type="button" className="apr-icon-btn cv-voice-btn" onClick={onPause} aria-label="Pause">
            <Icon name="pause" size={20} />
          </button>
        )}
        {state === 'paused' && (
          <button type="button" className="apr-icon-btn cv-voice-btn" onClick={onResume} aria-label="Resume">
            <Icon name="play" size={20} />
          </button>
        )}
        <button type="button" className="apr-btn cv-voice-stop" onClick={onStop} aria-label="Stop speaking">
          <Icon name="square" size={16} />
          Stop
        </button>
        <button type="button" className="apr-btn apr-btn--text cv-voice-type" onClick={onTypeInstead}>Type instead</button>
      </div>
    </div>
  );
}

/**
 * The crisis resources line: in a conversation that had a crisis card, the
 * email gate, the window's limit card and the questions-left line carry this
 * where a support line would be, so the way to a person is one tap away
 * while the box is closed. Under the device's 24-hour hold (utils/giving.js)
 * the limit card and the menu carry it too. The same words as every
 * failure's 988 line (utils/userFacingErrors.js); the full card is the
 * crisis link above.
 */
export function CrisisLine() {
  return (
    <p className="cv-crisis-line">
      <Icon name="phone" size={16} />
      <span>
        If you need to talk to someone right now, call or text <a href="tel:988">988</a>.
      </span>
    </p>
  );
}

/**
 * The support line (utils/giving.js): one quiet sentence, never louder than
 * the answer it sits under, with the give link in a new tab. No noreferrer,
 * but only the origin (https://askpastorrob.com/) goes along as the referrer
 * (firebase.json's Referrer-Policy), so nothing about the page leaves with it.
 *
 * While the support sheet is on (`sheet`), a plain tap opens the sheet
 * (GiveSheet) instead; a modified click still follows the link.
 *   surface    'answer' (under an answer, or the one being written) | 'limit'
 *              (the card that says the window's questions are used up)
 *   limitTime  when they refresh, for the sheet's first line (the limit card)
 */
export function SupportLine({ giveUrl, lead, surface = 'answer', sheet = false, limitTime = null }) {
  const opensSheet = sheet ? {
    'aria-haspopup': 'dialog',
    'data-give-opener': surface,
    'data-track': TRACK_OPEN[surface],
    onClick: (e) => openGiveFromClick(e, { surface, limitTime }),
  } : {};
  return (
    <p className="cv-support">
      <Icon name="heart" size={16} />
      <span>
        {lead} <a href={giveUrl} target="_blank" rel="noopener" {...opensSheet}>donate here</a>.
      </span>
    </p>
  );
}
